import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { TextField } from '@app/components/form-fields/TextField';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useChannelPartnerActions from '@app/hooks/channelPartners/useChannelPartnerActions';
import useChannelPartners from '@app/hooks/channelPartners/useChannelPartners';
import useModal from '@app/hooks/useModal';
import type { ChannelPartner, ChannelPartnerListParams } from '@app/services/api.types';
import { AppConfig } from '@app/utils/app-config';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd, MdSearch } from 'react-icons/md';
import { Link } from 'react-router';
import { PartnerAddModal } from './components/PartnerAddModal';
import { PartnerDeleteModal } from './components/PartnerDeleteModal';
import { PartnerEditModal } from './components/PartnerEditModal';
import PartnerDrawer from './components/PartnerDrawer';

export const ChannelPartnersList = () => {
    const [selectedRecord, setSelectedRecord] = useState<ChannelPartner | null>(null);
    const { exportChannelPartners } = useChannelPartnerActions();
    const [params, setParams] = useState<ChannelPartnerListParams>({
        page: 1,
        per_page: AppConfig.pagination_size,
        search: undefined,
        order_by: undefined,
        order_dir: undefined,
    });

    const { isLoading, data: channelPartnersData } = useChannelPartners(params);

    const modalAdd = useModal<ChannelPartner>({
        initialData: undefined,
    });
    const modalEdit = useModal<ChannelPartner>({
        initialData: undefined,
    });
    const modalRemove = useModal<ChannelPartner>({
        initialData: undefined,
    });

    const columns: TableColumnsType<ChannelPartner> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Admin Name',
            dataIndex: 'contact_name',
            key: 'contact_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Admin Email',
            dataIndex: 'contact_email',
            key: 'contact_email',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Admin Phone',
            dataIndex: 'contact_telephone',
            key: 'contact_telephone',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: false,
            width: 150,
            fixed: 'right',
            render: (_value, record) => {
                return (
                    <StyledButtonGroup justify="flex-start">
                        <StyledButton
                            icon={<HiOutlinePencil />}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                                modalEdit.open(record);
                            }}>
                            Edit
                        </StyledButton>
                        <StyledButton
                            icon={<HiOutlineTrash />}
                            color="red"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                                modalRemove.open(record);
                            }}>
                            Delete
                        </StyledButton>
                    </StyledButtonGroup>
                );
            },
        },
    ];

    const handleExport = async () => {
        exportChannelPartners.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="Channel Partners" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Channel Partners' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Channel Partners" />
                        <PageIntro>
                            <p>Manage channel partner organisations</p>
                        </PageIntro>
                    </div>
                    <div>
                        <StyledButtonGroup>
                            <StyledButton
                                type="primary"
                                color="blue"
                                icon={<MdAdd size={20} />}
                                onClick={() => modalAdd.open()}>
                                Add New
                            </StyledButton>
                        </StyledButtonGroup>
                    </div>
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <div className="form-fields-wrapper">
                            <TextField
                                placeholder="Search Channel Partners..."
                                prefix={<MdSearch size={20} />}
                                onChange={debounce((e) => {
                                    const value = e.target.value;
                                    setParams((prev) => ({
                                        ...prev,
                                        search: value,
                                        page: 1,
                                    }));
                                }, 500)}
                            />
                        </div>
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<ChannelPartner>
                        columns={columns}
                        dataSource={channelPartnersData?.data || []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: channelPartnersData?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <PartnerDrawer
                size={700}
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <PartnerAddModal
                open={modalAdd.isOpen}
                onCancel={() => modalAdd.reset()}
                onOk={() => modalAdd.reset()}
            />

            <PartnerEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={() => modalEdit.reset()}
                onOk={() => modalEdit.reset()}
            />

            <PartnerDeleteModal
                open={modalRemove.isOpen}
                data={modalRemove.data}
                onCancel={() => modalRemove.reset()}
                onOk={() => modalRemove.reset()}
            />
        </React.Fragment>
    );
};

export default ChannelPartnersList;
